import { useEffect, useCallback } from "react"
import { useLocation, useNavigate, Navigate } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { login as loginApi, logout as logoutApi, validateToken as validateTokenApi } from "api/authApi"
import {
  getToken,
  removeToken,
  setToken,
  getOriginalToken,
  getOriginalAuthUser,
  removeOriginalAuthUser,
  removeOriginalToken,
  getLastVisitedPath,
  setLastVisitedPath,
  removeLastVisitedPath,
} from "utils/helpersUtils"

export const AUTH_QUERY_KEY = "auth"

// Routes that should never be treated as a "resume here after login" target.
const RESUME_EXCLUDED_PATHS = ["/login", "/register", "/forgot-password", "/401"]

// Routes that should never be persisted as "last visited" (root redirects here anyway).
const LAST_VISITED_EXCLUDED_PATHS = ["/", ...RESUME_EXCLUDED_PATHS]

export const useLogin = () => {
  const queryClient = useQueryClient()
  const { login: authLogin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  return useMutation({
    mutationFn: loginApi,
    onSuccess: data => {
      const token = data?.data?.access_token
      const user = data?.data?.user

      // Store token if available
      if (token) {
        authLogin(token, user)
        const from = location.state?.from
        const resumeTarget = from?.pathname
          ? { pathname: from.pathname, search: from.search || "" }
          : "/"
        navigate(resumeTarget, { replace: true })
      }

      // Invalidate any existing auth queries to refresh user data
      queryClient.invalidateQueries([AUTH_QUERY_KEY])
    },
  })
}

// Helper function for logout cleanup
const handleLogoutCleanup = (queryClient, navigate, resumeFrom) => {
  removeToken()
  removeOriginalAuthUser()
  removeOriginalToken()
  removeLastVisitedPath()
  queryClient.removeQueries([AUTH_QUERY_KEY])
  queryClient.clear() // Optional: clear all queries if needed
  navigate("/login", {
    replace: true,
    ...(resumeFrom ? { state: { from: resumeFrom } } : {}),
  })
}

export const useLogout = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  const resumeFrom = RESUME_EXCLUDED_PATHS.includes(location.pathname)
    ? null
    : { pathname: location.pathname, search: location.search }

  return useMutation({
    retry: 1, // Retry only once on failure
    retryDelay: 1000, // Wait 1 second before retrying
    mutationFn: logoutApi,
    onSuccess: () => {
      handleLogoutCleanup(queryClient, navigate, resumeFrom)
    },
    onError: error => {
      handleLogoutCleanup(queryClient, navigate, resumeFrom)
    },
  })
}

export const useValidateToken = () => {
  return useQuery({
    queryKey: [AUTH_QUERY_KEY, "validate"],
    queryFn: validateTokenApi,
    retry: false,
    enabled: !!getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  })
}

// Enhanced auth hook with token validation
export const useAuth = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const {
    data: validationData,
    isLoading: isValidating,
    error: validationError,
    refetch: refetchValidation,
  } = useValidateToken()

  const logoutMutation = useLogout()

  const user = validationData?.data?.user ?? null
  const originalUser = getOriginalAuthUser()

  const permissions = user?.access ?? []
  const roles = user?.roles ?? []
  const isAuthenticated = !!validationData?.data?.valid
  const isLoading = isValidating

  // Handle invalid token
  useEffect(() => {
    if (!isValidating && validationError) removeToken()
  }, [isValidating, validationError])

  const login = useCallback((token, userData = null) => {
    setToken(token)
    if (userData) {
      queryClient.setQueryData([AUTH_QUERY_KEY, "validate"], {
        data: { valid: true, user: userData },
      })
    }
    refetchValidation()
  }, [queryClient, refetchValidation])

  const logout = useCallback(async () => await logoutMutation.mutateAsync(), [logoutMutation])

  const isImpersonating = !!getOriginalToken()

  const stopImpersonating = useCallback(async () => {
    const originalToken = getOriginalToken()
    if (!originalToken) {
      return
    }

    try {
      await logoutApi()
    } catch (error) {
    } finally {
      setToken(originalToken)
      removeOriginalAuthUser()
      removeOriginalToken()
      queryClient.removeQueries([AUTH_QUERY_KEY])
      queryClient.invalidateQueries([AUTH_QUERY_KEY, "validate"])
      navigate("/", { replace: true })
    }
  }, [queryClient, navigate])

  const checkAuth = () => !!getToken() && isAuthenticated

  // Permission helper
  const hasPermission = perm => permissions.includes(perm)
  const hasAnyPermission = (permList = []) =>
    permList.some(p => permissions.includes(p))

  const hasRole = roleSlug => roles.some(r => r.slug === roleSlug)

  return {
    isAuthenticated,
    isLoading: isLoading && !!getToken(),
    user,
    roles,
    permissions,
    originalUser,
    login,
    logout,
    checkAuth,
    hasPermission,
    hasAnyPermission,
    hasRole,
    validationError,
    refetchValidation,
    isImpersonating,
    stopImpersonating,
  }
}

// Hook for protecting public routes (like login) - redirect if already logged in
export const usePublicRoute = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Redirect to home or intended page if already logged in
      const from = location.state?.from
      const resumeTarget = from?.pathname
        ? { pathname: from.pathname, search: from.search || "" }
        : "/"
      navigate(resumeTarget, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, location])

  return { isLoading, isAuthenticated }
}

// Hook for protecting private routes - redirect to login if not authenticated
export const usePrivateRoute = () => {
  const { isAuthenticated, isLoading, refetchValidation } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const token = getToken()
    if (token && !isLoading) {
      refetchValidation()
    }
  }, [])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to login page with return url
      navigate("/login", {
        state: { from: location },
        replace: true,
      })
    }
  }, [isAuthenticated, isLoading, navigate, location])

  return { isLoading, isAuthenticated }
}

// Higher Order Component for public routes (login, register, etc.)
export const PublicRoute = ({ children }) => {
  const { isLoading } = usePublicRoute()

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return children
}
// Higher Order Component for private routes
export const PrivateRoute = ({ children, requiredPermissions = [] }) => {
  const { isLoading, isAuthenticated, hasAnyPermission } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login", { replace: true })
    }
  }, [isLoading, isAuthenticated, navigate])

  // Remember the current page so the root path can resume here on a later visit.
  useEffect(() => {
    if (isAuthenticated && !LAST_VISITED_EXCLUDED_PATHS.includes(location.pathname)) {
      setLastVisitedPath({ pathname: location.pathname, search: location.search })
    }
  }, [isAuthenticated, location.pathname, location.search])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  // Permission check (handled after authentication)
  if (
    !isLoading &&
    isAuthenticated &&
    requiredPermissions.length &&
    !hasAnyPermission(requiredPermissions)
  ) {
    return navigate("/401", { replace: true })
  }

  // Render protected children only if authenticated
  return isAuthenticated ? children : null
}

// Redirect target for the root path ("/") - resumes the last visited page on this
// device if one was saved, otherwise falls back to the dashboard.
export const ResumeRedirect = () => {
  const lastVisited = getLastVisitedPath()

  if (lastVisited?.pathname && lastVisited.pathname !== "/") {
    return <Navigate to={`${lastVisited.pathname}${lastVisited.search || ""}`} replace />
  }

  return <Navigate to="/dashboard" replace />
}

// Custom hook for components that need auth state
export const useAuthState = () => {
  const { isAuthenticated, isLoading, user } = useAuth()

  return {
    isAuthenticated,
    isLoading,
    user,
    isAdmin: user?.role === "admin", // Example role check
    isUserLoaded: !!user && !isLoading,
  }
}
